import { Theme } from '@monorepo/relayably/theme'
import { RelayablyInstructionItem, RelayablyKnowledgeItem, RelayablyVoiceOption, RelayablyWorkspaceDocument } from '@monorepo/relayably/workspaces'
import { Audio, AVPlaybackStatus, InterruptionModeAndroid, InterruptionModeIOS } from 'expo-av'
import * as DocumentPicker from 'expo-document-picker'
import * as FileSystem from 'expo-file-system'
import { Play, Stop } from 'phosphor-react-native'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ActivityIndicator, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View, useWindowDimensions } from 'react-native'
import { DashboardAgentSummary } from '../types'
import { relayablyWorkspaceApi } from '../../workspace/relayablyWorkspaceApi'

export type AgentsTopTab = 'agents' | 'voice' | 'knowledge' | 'instructions'

const emptyKnowledgeDraft = {
  id: '',
  name: '',
  type: 'text' as 'text' | 'url',
  content: '',
  url: '',
}

const emptyInstructionDraft = {
  id: '',
  content: '',
}

const SUPPORTED_KNOWLEDGE_FILE_TYPES = [
  'application/pdf',
  'text/plain',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const

const SUPPORTED_KNOWLEDGE_FILE_EXTENSIONS = ['pdf', 'txt', 'doc', 'docx'] as const

const SUPPORTED_KNOWLEDGE_FILE_LABEL = 'PDF, TXT, DOC, DOCX'

const readUriAsBase64 = async (uri: string) => {
  if (Platform.OS === 'web') {
    const response = await fetch(uri)
    const blob = await response.blob()

    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result).split(',').pop() ?? '')
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(blob)
    })
  }

  return FileSystem.readAsStringAsync(uri, {
    encoding: FileSystem.EncodingType.Base64,
  })
}

const hexToRgba = (hex: string, alpha: number) => {
  const value = hex.replace('#', '')
  const expanded =
    value.length === 3
      ? value
          .split('')
          .map(char => `${char}${char}`)
          .join('')
      : value
  const r = parseInt(expanded.slice(0, 2), 16)
  const g = parseInt(expanded.slice(2, 4), 16)
  const b = parseInt(expanded.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const formatDuration = (seconds: number) => {
  const minutes = Math.round(seconds / 60)
  return minutes ? `${minutes}m` : '0m'
}

const toProperCase = (value: string) =>
  value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map(token => token.charAt(0).toUpperCase() + token.slice(1).toLowerCase())
    .join(' ')

const matchesVoiceFilters = ({
  voice,
  accent,
  gender,
  age,
}: {
  voice: RelayablyVoiceOption
  accent: string
  gender: 'any' | 'female' | 'male'
  age: 'any' | 'young' | 'middle_aged' | 'old'
}) => {
  if (gender !== 'any' && voice.gender !== gender) {
    return false
  }

  if (age !== 'any' && voice.age !== age) {
    return false
  }

  if (accent && voice.accent !== accent.toLowerCase()) {
    return false
  }

  return true
}

const voiceGenderFilters = [
  { label: 'Any', value: 'any' as const },
  { label: 'Female', value: 'female' as const },
  { label: 'Male', value: 'male' as const },
]

const voiceAgeFilters = [
  { label: 'Any', value: 'any' as const },
  { label: 'Young', value: 'young' as const },
  { label: 'Middle Aged', value: 'middle_aged' as const },
  { label: 'Older', value: 'old' as const },
]

const isValidWebsite = (value: string) => {
  const trimmed = value.trim()
  if (!trimmed) {
    return true
  }

  if (/\s/.test(trimmed)) {
    return false
  }

  try {
    const normalized = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
    const parsed = new URL(normalized)
    return parsed.hostname.includes('.') && !parsed.hostname.startsWith('.')
  } catch {
    return false
  }
}

const isSupportedKnowledgeFile = ({
  fileName,
  mimeType,
}: {
  fileName: string
  mimeType?: string | null
}) => {
  const extension = fileName.split('.').pop()?.toLowerCase() ?? ''
  return SUPPORTED_KNOWLEDGE_FILE_TYPES.includes((mimeType ?? '') as (typeof SUPPORTED_KNOWLEDGE_FILE_TYPES)[number]) || SUPPORTED_KNOWLEDGE_FILE_EXTENSIONS.includes(extension as (typeof SUPPORTED_KNOWLEDGE_FILE_EXTENSIONS)[number])
}

const getKnowledgeDropState = (files: Array<{ name: string; type?: string | null }>) => {
  if (!files.length) {
    return null
  }

  return files.every(file => isSupportedKnowledgeFile({ fileName: file.name, mimeType: file.type }))
    ? 'ready'
    : 'unsupported'
}

const AgentsList = ({
  theme,
  agents,
  onOpenAgent,
}: {
  theme: Theme
  agents: DashboardAgentSummary[]
  onOpenAgent: (agent: DashboardAgentSummary) => void
}) => {
  const styles = createStyles(theme)

  return (
    <View style={styles.grid}>
      {agents.map(agent => (
        <Pressable key={agent.id} onPress={() => onOpenAgent(agent)} style={styles.card}>
          <View style={styles.cardTop}>
            <View style={styles.avatar}>
              <Text style={styles.avatarText}>{agent.name.charAt(0)}</Text>
            </View>
            <View style={styles.statusBadge}>
              <Text style={styles.statusBadgeText}>{agent.status}</Text>
            </View>
          </View>
          <Text style={styles.name}>{agent.name}</Text>
          {agent.personaName ? <Text style={styles.personaLabel}>{agent.personaName}</Text> : null}
          <Text style={styles.description}>{agent.description}</Text>
          <View style={styles.metaRow}>
            <Text style={styles.metaText}>{agent.calls} calls</Text>
            <Text style={styles.metaText}>{formatDuration(agent.totalDurationSeconds)} talk time</Text>
          </View>
          <Text style={styles.voiceLabel}>{agent.voiceLabel}</Text>
        </Pressable>
      ))}
    </View>
  )
}

const VoiceManager = ({
  theme,
  sessionToken,
  agents,
  workspace,
  selectedVoice,
  onSelectVoice,
  onSelectAgentVoice,
}: {
  theme: Theme
  sessionToken: string | null
  agents: DashboardAgentSummary[]
  workspace: RelayablyWorkspaceDocument | null
  selectedVoice: RelayablyVoiceOption | null
  onSelectVoice: (voice: RelayablyVoiceOption) => void
  onSelectAgentVoice: (agentId: string, voice: RelayablyVoiceOption | null) => void
}) => {
  const styles = createStyles(theme)
  const { width } = useWindowDimensions()
  const [accent, setAccent] = useState('')
  const [gender, setGender] = useState<'any' | 'female' | 'male'>('any')
  const [age, setAge] = useState<'any' | 'young' | 'middle_aged' | 'old'>('any')
  const [voices, setVoices] = useState<RelayablyVoiceOption[]>([])
  const [availableAccents, setAvailableAccents] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [previewLoadingVoiceId, setPreviewLoadingVoiceId] = useState<string | null>(null)
  const [previewPlayingVoiceId, setPreviewPlayingVoiceId] = useState<string | null>(null)
  const previewSoundRef = useRef<Audio.Sound | null>(null)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const filterLayout = width >= 1180 ? 'wide' : width >= 760 ? 'medium' : 'compact'
  const voiceGridColumns = width >= 1380 ? 4 : width >= 1080 ? 3 : width >= 760 ? 2 : 1
  const voiceTileWidth = useMemo(() => {
    const outerPadding = width >= 760 ? 40 : 0
    const totalGap = (voiceGridColumns - 1) * 16
    const availableWidth = Math.max(240, width - outerPadding - totalGap)
    return Math.floor(availableWidth / voiceGridColumns)
  }, [voiceGridColumns, width])

  const loadVoices = async () => {
    if (!sessionToken) {
      return
    }

    setLoading(true)
    setErrorMessage(null)

    try {
      const response = await relayablyWorkspaceApi.listVoices(sessionToken, {
        pageSize: 500,
      })
      setVoices(response.voices)
      setAvailableAccents(response.availableAccents)
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Relayably could not load voices right now.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadVoices()
    return () => {
      if (previewSoundRef.current) {
        void previewSoundRef.current.stopAsync().catch(() => undefined)
        void previewSoundRef.current.unloadAsync().catch(() => undefined)
        previewSoundRef.current = null
      }
    }
  }, [sessionToken])

  const stopPreview = async () => {
    if (!previewSoundRef.current) {
      return
    }

    try {
      await previewSoundRef.current.stopAsync()
      await previewSoundRef.current.unloadAsync()
    } finally {
      previewSoundRef.current = null
      setPreviewPlayingVoiceId(null)
      setPreviewLoadingVoiceId(null)
    }
  }

  const handlePreview = async (voice: RelayablyVoiceOption) => {
    if (!voice.previewUrl) {
      return
    }

    if (previewPlayingVoiceId === voice.id) {
      await stopPreview()
      return
    }

    setErrorMessage(null)
    setPreviewLoadingVoiceId(voice.id)

    try {
      await stopPreview()
      await Audio.setAudioModeAsync({
        allowsRecordingIOS: false,
        interruptionModeAndroid: InterruptionModeAndroid.DoNotMix,
        interruptionModeIOS: InterruptionModeIOS.DoNotMix,
        playsInSilentModeIOS: true,
        shouldDuckAndroid: true,
        staysActiveInBackground: false,
      })
      const { sound } = await Audio.Sound.createAsync(
        { uri: voice.previewUrl },
        { shouldPlay: true },
        (status: AVPlaybackStatus) => {
          if (!status.isLoaded) {
            return
          }

          if (status.didJustFinish) {
            void stopPreview()
          }
        }
      )

      previewSoundRef.current = sound
      setPreviewPlayingVoiceId(voice.id)
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Relayably could not preview that voice.')
      await stopPreview()
    } finally {
      setPreviewLoadingVoiceId(current => (current === voice.id ? null : current))
    }
  }

  const voiceCatalogById = useMemo(() => new Map(voices.map(voice => [voice.id, voice])), [voices])

  const resolvedSelectedVoice = useMemo(() => {
    if (!selectedVoice) {
      return null
    }

    return voiceCatalogById.get(selectedVoice.id) ?? selectedVoice
  }, [selectedVoice, voiceCatalogById])

  const visibleVoices = useMemo(() => {
    const locallyFiltered = voices.filter(voice =>
      matchesVoiceFilters({
        voice,
        accent,
        gender,
        age,
      })
    )

    if (!resolvedSelectedVoice) {
      return locallyFiltered
    }

    const deduped = new Map<string, RelayablyVoiceOption>([[resolvedSelectedVoice.id, resolvedSelectedVoice]])
    locallyFiltered.forEach(voice => {
      if (!deduped.has(voice.id)) {
        deduped.set(voice.id, voice)
      }
    })

    return Array.from(deduped.values())
  }, [accent, age, gender, resolvedSelectedVoice, voices])

  return (
    <View style={styles.stack}>
      <Text style={styles.supportingCopy}>Choose the voice Relayably should use. Filter by accent, gender, and age.</Text>

      {resolvedSelectedVoice ? (
        <View style={styles.currentVoiceBar}>
          <View style={styles.currentVoiceContent}>
            <Text style={styles.currentVoiceLabel}>Current default voice</Text>
            <Text style={styles.currentVoiceName} numberOfLines={1}>
              {resolvedSelectedVoice.name}
            </Text>
            <View style={styles.voiceTagRow}>
              {[resolvedSelectedVoice.accent, resolvedSelectedVoice.gender, voiceAgeFilters.find(filter => filter.value === resolvedSelectedVoice.age)?.label ?? '']
                .filter(Boolean)
                .map(tag => (
                  <View key={`selected-${tag}`} style={styles.voiceTag}>
                    <Text style={styles.voiceTagText}>{toProperCase(tag)}</Text>
                  </View>
                ))}
            </View>
          </View>
          <View style={styles.voiceIconButtons}>
            {resolvedSelectedVoice.previewUrl ? (
              <Pressable onPress={() => void handlePreview(resolvedSelectedVoice)} style={styles.iconButton}>
                {previewLoadingVoiceId === resolvedSelectedVoice.id ? (
                  <ActivityIndicator color={theme.colors.text} size='small' />
                ) : previewPlayingVoiceId === resolvedSelectedVoice.id ? (
                  <Stop size={18} color={theme.colors.text} weight='fill' />
                ) : (
                  <Play size={18} color={theme.colors.text} weight='fill' />
                )}
              </Pressable>
            ) : null}
          </View>
        </View>
      ) : null}

      <View
        style={[
          styles.filterBlock,
          filterLayout === 'wide' ? styles.filterBlockWide : undefined,
          filterLayout === 'medium' ? styles.filterBlockMedium : undefined,
        ]}
      >
        <View style={[styles.filterSection, filterLayout === 'wide' ? styles.filterSectionWide : undefined]}>
          <Text style={styles.filterLabel}>Accent</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.compactChipRow}>
            <Pressable onPress={() => setAccent('')} style={[styles.filterPill, !accent ? styles.filterPillActive : undefined]}>
              <Text style={[styles.filterPillText, !accent ? styles.filterPillTextActive : undefined]}>Any</Text>
            </Pressable>
            {availableAccents.map(item => (
              <Pressable key={item} onPress={() => setAccent(item)} style={[styles.filterPill, accent === item ? styles.filterPillActive : undefined]}>
                <Text style={[styles.filterPillText, accent === item ? styles.filterPillTextActive : undefined]}>{item}</Text>
              </Pressable>
            ))}
          </ScrollView>
        </View>
        <View style={[styles.filterSection, filterLayout !== 'compact' ? styles.filterSectionStacked : undefined]}>
          <View style={styles.filterRow}>
            <Text style={styles.filterLabel}>Gender</Text>
            <View style={styles.compactToggleRow}>
              {voiceGenderFilters.map(filter => (
                <Pressable
                  key={filter.value}
                  onPress={() => setGender(filter.value)}
                  style={[styles.filterPill, gender === filter.value ? styles.filterPillActive : undefined]}
                >
                  <Text style={[styles.filterPillText, gender === filter.value ? styles.filterPillTextActive : undefined]}>{filter.label}</Text>
                </Pressable>
              ))}
            </View>
          </View>
          <View style={styles.filterRow}>
            <Text style={styles.filterLabel}>Age</Text>
            <View style={styles.compactToggleRow}>
              {voiceAgeFilters.map(filter => (
                <Pressable
                  key={filter.value}
                  onPress={() => setAge(filter.value)}
                  style={[styles.filterPill, age === filter.value ? styles.filterPillActive : undefined]}
                >
                  <Text style={[styles.filterPillText, age === filter.value ? styles.filterPillTextActive : undefined]}>{filter.label}</Text>
                </Pressable>
              ))}
            </View>
          </View>
        </View>
      </View>

      {errorMessage ? <Text style={styles.errorText}>{errorMessage}</Text> : null}
      {loading ? (
        <View style={styles.loaderWrap}>
          <ActivityIndicator color={theme.colors.primary} />
        </View>
      ) : (
        <>
          <View style={styles.grid}>
            {visibleVoices.map(voice => {
              const selected = resolvedSelectedVoice?.id === voice.id
              return (
                <View
                  key={voice.id}
                  style={[
                    styles.voiceTile,
                    selected ? styles.voiceTileSelected : undefined,
                    {
                      width: voiceTileWidth,
                      flexGrow: 0,
                      flexShrink: 0,
                      maxWidth: voiceTileWidth,
                    },
                  ]}
                >
                  <View style={styles.voiceTileTop}>
                    <View style={styles.voiceTileHeader}>
                      <Text style={styles.voiceTileName} numberOfLines={2}>
                        {voice.name}
                      </Text>
                      {selected ? (
                        <View style={styles.selectedBadge}>
                          <Text style={styles.selectedBadgeText}>Selected</Text>
                        </View>
                      ) : null}
                    </View>
                  </View>
                  <View style={styles.voiceTagRow}>
                    {[voice.accent, voice.gender, voiceAgeFilters.find(filter => filter.value === voice.age)?.label ?? '']
                      .filter(Boolean)
                      .map(tag => (
                        <View key={`${voice.id}-${tag}`} style={styles.voiceTag}>
                          <Text style={styles.voiceTagText}>{toProperCase(tag)}</Text>
                        </View>
                      ))}
                  </View>
                  <View style={styles.voiceTileActions}>
                    <Pressable onPress={() => onSelectVoice(voice)} style={[styles.primaryButton, styles.voiceSelectButton, selected ? styles.voiceSelectButtonSelected : undefined]}>
                      <Text style={styles.primaryButtonLabel}>{selected ? 'Selected' : 'Select'}</Text>
                    </Pressable>
                    {voice.previewUrl ? (
                      <Pressable onPress={() => void handlePreview(voice)} style={styles.iconButton}>
                        {previewLoadingVoiceId === voice.id ? (
                          <ActivityIndicator color={theme.colors.text} size='small' />
                        ) : previewPlayingVoiceId === voice.id ? (
                          <Stop size={18} color={theme.colors.text} weight='fill' />
                        ) : (
                          <Play size={18} color={theme.colors.text} weight='fill' />
                        )}
                      </Pressable>
                    ) : (
                      <View style={styles.iconButtonPlaceholder} />
                    )}
                  </View>
                </View>
              )
            })}
          </View>
          <View style={styles.panelSection}>
            <Pressable onPress={() => setShowAdvanced(current => !current)} style={styles.advancedToggle}>
              <Text style={styles.sectionTitle}>Advanced</Text>
              <Text style={styles.advancedToggleText}>{showAdvanced ? 'Hide per-agent voices' : 'Choose a voice per agent'}</Text>
            </Pressable>
            {showAdvanced ? (
              <View style={styles.stack}>
                <Text style={styles.supportingCopy}>Leave an agent on default, or give it its own voice.</Text>
                {agents.map(agent => {
                  const overrideVoice =
                    workspace?.workspace.agentConfigs.find(config => config.id === agent.id)?.voiceSelection ?? null
                  const activeVoice = overrideVoice ? (voiceCatalogById.get(overrideVoice.id) ?? overrideVoice) : resolvedSelectedVoice

                  return (
                    <View key={agent.id} style={styles.itemCard}>
                      <View style={styles.itemHeader}>
                        <View style={styles.agentVoiceHeader}>
                          <Text style={styles.itemTitle}>{agent.name}</Text>
                          {agent.personaName ? <Text style={styles.personaInline}>{agent.personaName}</Text> : null}
                        </View>
                        <Text style={styles.itemBadge}>{overrideVoice ? 'Custom' : 'Default'}</Text>
                      </View>
                      <Text style={styles.itemBody}>
                        {activeVoice
                          ? `${activeVoice.name}${activeVoice.accent ? ` • ${activeVoice.accent}` : ''}`
                          : 'No voice selected yet'}
                      </Text>
                      <View style={styles.actionRow}>
                        <Pressable onPress={() => activeVoice && onSelectAgentVoice(agent.id, activeVoice)} style={styles.primaryButton} disabled={!activeVoice}>
                          <Text style={styles.primaryButtonLabel}>Use current result</Text>
                        </Pressable>
                        {overrideVoice ? (
                          <Pressable onPress={() => onSelectAgentVoice(agent.id, null)} style={styles.secondaryButton}>
                            <Text style={styles.secondaryButtonText}>Use default voice</Text>
                          </Pressable>
                        ) : null}
                      </View>
                    </View>
                  )
                })}
              </View>
            ) : null}
          </View>
        </>
      )}
    </View>
  )
}

const KnowledgeManager = ({
  theme,
  workspace,
  onSaveCompanyProfile,
  onSaveKnowledgeItem,
  onDeleteKnowledgeItem,
  onUploadKnowledgeFile,
}: {
  theme: Theme
  workspace: RelayablyWorkspaceDocument | null
  onSaveCompanyProfile: (payload: { businessName: string; website: string; businessDescription: string }) => void
  onSaveKnowledgeItem: (payload: { id?: string; name: string; type: 'text' | 'url'; content?: string; url?: string }) => void
  onDeleteKnowledgeItem: (knowledgeItemId: string) => void
  onUploadKnowledgeFile: (payload: { name?: string; fileName: string; mimeType?: string; base64: string }) => void
}) => {
  const styles = createStyles(theme)
  const [businessName, setBusinessName] = useState(workspace?.workspace.businessName ?? '')
  const [website, setWebsite] = useState(workspace?.workspace.website ?? '')
  const [businessDescription, setBusinessDescription] = useState(workspace?.workspace.businessDescription ?? '')
  const [knowledgeDraft, setKnowledgeDraft] = useState(emptyKnowledgeDraft)
  const [dragState, setDragState] = useState<'ready' | 'unsupported' | null>(null)
  const [stagedKnowledgeFile, setStagedKnowledgeFile] = useState<{
    fileName: string
    mimeType?: string
    base64: string
  } | null>(null)

  useEffect(() => {
    setBusinessName(workspace?.workspace.businessName ?? '')
    setWebsite(workspace?.workspace.website ?? '')
    setBusinessDescription(workspace?.workspace.businessDescription ?? '')
  }, [workspace?.workspace.businessDescription, workspace?.workspace.businessName, workspace?.workspace.website])

  const knowledgeItems = workspace?.workspace.knowledgeItems ?? []
  const showCompanyProfile = workspace?.workspace.usageType !== 'personal'

  const handleEditKnowledge = (item: RelayablyKnowledgeItem) => {
    setKnowledgeDraft({
      id: item.id,
      name: item.name,
      type: item.type === 'url' ? 'url' : 'text',
      content: item.content ?? '',
      url: item.url ?? '',
    })
  }

  const handlePickKnowledgeFile = async () => {
    const result = await DocumentPicker.getDocumentAsync({
      copyToCacheDirectory: true,
      multiple: false,
    })

    if (result.canceled) {
      return
    }

    const asset = result.assets[0]
    if (!isSupportedKnowledgeFile({ fileName: asset.name, mimeType: asset.mimeType })) {
      setDragState('unsupported')
      return
    }

    const base64 = await readUriAsBase64(asset.uri)

    setKnowledgeDraft(current =>
      current.name
        ? current
        : {
            ...current,
            name: asset.name.replace(/\.[^.]+$/, ''),
          }
    )
    setStagedKnowledgeFile({
      fileName: asset.name,
      mimeType: asset.mimeType ?? 'application/octet-stream',
      base64,
    })
    setDragState(null)
  }

  const handleDropKnowledgeFiles = async (event: any) => {
    if (Platform.OS !== 'web') {
      return
    }

    event.preventDefault?.()
    const droppedFiles = Array.from(event?.dataTransfer?.files ?? []) as File[]
    const nextDragState = getKnowledgeDropState(droppedFiles)
    setDragState(null)

    if (!droppedFiles.length) {
      return
    }

    const file = droppedFiles[0]
    if (nextDragState === 'unsupported') {
      setStagedKnowledgeFile(null)
      setDragState('unsupported')
      return
    }

    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result).split(',').pop() ?? '')
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(file)
    })

    setKnowledgeDraft(current =>
      current.name
        ? current
        : {
            ...current,
            name: file.name.replace(/\.[^.]+$/, ''),
          }
    )
    setStagedKnowledgeFile({
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      base64,
    })
  }

  const handleUploadStagedKnowledgeFile = () => {
    if (!stagedKnowledgeFile || !knowledgeDraft.name.trim()) {
      return
    }

    onUploadKnowledgeFile({
      name: knowledgeDraft.name.trim(),
      fileName: stagedKnowledgeFile.fileName,
      mimeType: stagedKnowledgeFile.mimeType,
      base64: stagedKnowledgeFile.base64,
    })
    setStagedKnowledgeFile(null)
    setKnowledgeDraft(emptyKnowledgeDraft)
    setDragState(null)
  }

  return (
    <View style={styles.stack}>
      {showCompanyProfile ? (
        <View style={styles.panelSection}>
          <Text style={styles.sectionTitle}>Company profile</Text>
          <Text style={styles.supportingCopy}>This is your main knowledge item. Keep it current so the agents know who they represent.</Text>
          <TextInput value={businessName} onChangeText={setBusinessName} style={styles.input} placeholder='Business name' placeholderTextColor={theme.colors.inactive} />
          <TextInput value={website} onChangeText={setWebsite} style={styles.input} placeholder='Website' placeholderTextColor={theme.colors.inactive} />
          {!isValidWebsite(website) ? <Text style={styles.errorText}>Enter a valid website or leave it blank.</Text> : null}
          <TextInput
            value={businessDescription}
            onChangeText={setBusinessDescription}
            style={[styles.input, styles.multiline]}
            multiline
            placeholder='Short business description'
            placeholderTextColor={theme.colors.inactive}
          />
          <Pressable onPress={() => onSaveCompanyProfile({ businessName, website, businessDescription })} style={styles.primaryButton}>
            <Text style={styles.primaryButtonLabel}>Save company profile</Text>
          </Pressable>
        </View>
      ) : null}

      <View
        style={[styles.panelSection, dragState ? styles.dropPanelActive : undefined]}
        {...(Platform.OS === 'web'
          ? ({
              onDragOver: (event: any) => {
                event.preventDefault?.()
                const hoveredFiles = Array.from(event?.dataTransfer?.items ?? []).map((item: DataTransferItem) => ({
                  name: item.getAsFile?.()?.name ?? '',
                  type: item.type,
                }))
                setDragState(getKnowledgeDropState(hoveredFiles))
              },
              onDragLeave: () => setDragState(null),
              onDrop: (event: any) => void handleDropKnowledgeFiles(event),
            } as any)
          : {})}
      >
        {dragState ? (
          <View pointerEvents='none' style={styles.dropOverlay}>
            <View style={[styles.dropOverlayCard, dragState === 'unsupported' ? styles.dropOverlayCardInvalid : undefined]}>
              <Text style={styles.dropOverlayTitle}>{dragState === 'unsupported' ? 'Unsupported file type.' : 'Drop file here'}</Text>
              <Text style={styles.dropOverlayBody}>Supported file types: {SUPPORTED_KNOWLEDGE_FILE_LABEL}</Text>
            </View>
          </View>
        ) : null}
        <Text style={styles.sectionTitle}>Knowledge items</Text>
        <Text style={styles.supportingCopy}>Drag a document here or choose a file, then give it a knowledge name and press upload.</Text>
        <View style={styles.stack}>
          {knowledgeItems.map(item => (
            <View key={item.id} style={styles.itemCard}>
              <View style={styles.itemHeader}>
                <Text style={styles.itemTitle}>{item.name}</Text>
                <Text style={styles.itemBadge}>{item.type === 'file' ? 'File' : item.type === 'url' ? 'Link' : 'Text'}</Text>
              </View>
              <Text style={styles.itemBody}>
                {item.type === 'url' ? item.url : item.type === 'file' ? item.fileName || 'Uploaded file' : item.content || 'No content yet'}
              </Text>
              <View style={styles.actionRow}>
                {item.type !== 'file' ? (
                  <Pressable onPress={() => handleEditKnowledge(item)} style={styles.secondaryButton}>
                    <Text style={styles.secondaryButtonText}>Edit</Text>
                  </Pressable>
                ) : null}
                <Pressable onPress={() => onDeleteKnowledgeItem(item.id)} style={styles.destructiveButton}>
                  <Text style={styles.destructiveButtonText}>Delete</Text>
                </Pressable>
              </View>
            </View>
          ))}
        </View>

        {stagedKnowledgeFile ? (
          <View style={styles.stagedFileCard}>
            <Text style={styles.itemTitle}>Ready to upload</Text>
            <Text style={styles.itemBody}>{stagedKnowledgeFile.fileName}</Text>
            <Text style={styles.itemHint}>Add or confirm the knowledge name below, then press upload.</Text>
            <View style={styles.actionRow}>
              <Pressable onPress={() => setStagedKnowledgeFile(null)} style={styles.secondaryButton}>
                <Text style={styles.secondaryButtonText}>Remove file</Text>
              </Pressable>
            </View>
          </View>
        ) : null}

        <View style={styles.toggleRow}>
          {(['text', 'url'] as const).map(type => (
            <Pressable
              key={type}
              onPress={() => setKnowledgeDraft(current => ({ ...current, type }))}
              style={[styles.toggleChip, knowledgeDraft.type === type ? styles.toggleChipActive : undefined]}
            >
              <Text style={[styles.toggleChipText, knowledgeDraft.type === type ? styles.toggleChipTextActive : undefined]}>
                {type === 'text' ? 'Text note' : 'Website'}
              </Text>
            </Pressable>
          ))}
        </View>
        <TextInput
          value={knowledgeDraft.name}
          onChangeText={value => setKnowledgeDraft(current => ({ ...current, name: value }))}
          style={styles.input}
          placeholder='Knowledge name'
          placeholderTextColor={theme.colors.inactive}
        />
        {knowledgeDraft.type === 'text' ? (
          <TextInput
            value={knowledgeDraft.content}
            onChangeText={value => setKnowledgeDraft(current => ({ ...current, content: value }))}
            style={[styles.input, styles.multiline]}
            multiline
            placeholder='Write the knowledge the agents should use.'
            placeholderTextColor={theme.colors.inactive}
          />
        ) : (
          <TextInput
            value={knowledgeDraft.url}
            onChangeText={value => setKnowledgeDraft(current => ({ ...current, url: value }))}
            style={styles.input}
            placeholder='https://example.com'
            placeholderTextColor={theme.colors.inactive}
          />
        )}
        <View style={styles.actionRow}>
          {stagedKnowledgeFile ? (
            <Pressable onPress={handleUploadStagedKnowledgeFile} style={[styles.primaryButton, !knowledgeDraft.name.trim() ? styles.buttonDisabled : undefined]} disabled={!knowledgeDraft.name.trim()}>
              <Text style={styles.primaryButtonLabel}>Upload knowledge</Text>
            </Pressable>
          ) : (
            <Pressable
              onPress={() => {
                onSaveKnowledgeItem(knowledgeDraft)
                setKnowledgeDraft(emptyKnowledgeDraft)
              }}
              style={styles.primaryButton}
            >
              <Text style={styles.primaryButtonLabel}>{knowledgeDraft.id ? 'Save changes' : 'Add knowledge'}</Text>
            </Pressable>
          )}
          {knowledgeDraft.id ? (
            <Pressable onPress={() => setKnowledgeDraft(emptyKnowledgeDraft)} style={styles.secondaryButton}>
              <Text style={styles.secondaryButtonText}>Cancel</Text>
            </Pressable>
          ) : null}
          <Pressable onPress={() => void handlePickKnowledgeFile()} style={styles.secondaryButton}>
            <Text style={styles.secondaryButtonText}>Upload file</Text>
          </Pressable>
        </View>
      </View>
    </View>
  )
}

const InstructionsManager = ({
  theme,
  workspace,
  onSaveInstruction,
  onDeleteInstruction,
}: {
  theme: Theme
  workspace: RelayablyWorkspaceDocument | null
  onSaveInstruction: (payload: { id?: string; content: string }) => void
  onDeleteInstruction: (instructionId: string) => void
}) => {
  const styles = createStyles(theme)
  const [instructionDraft, setInstructionDraft] = useState(emptyInstructionDraft)
  const instructions = workspace?.workspace.instructions ?? []

  const handleEditInstruction = (item: RelayablyInstructionItem) => {
    setInstructionDraft({
      id: item.id,
      content: item.content,
    })
  }

  return (
    <View style={styles.stack}>
      <Text style={styles.supportingCopy}>Instructions tell Relayably how to behave in specific situations and are applied back to your agents.</Text>
      {instructions.map(item => (
        <View key={item.id} style={styles.itemCard}>
          <View style={styles.itemHeader}>
            <Text style={styles.itemTitle}>Instruction</Text>
            <Text style={styles.itemBadge}>Instruction</Text>
          </View>
          <Text style={styles.itemBody}>{item.content}</Text>
          {item.intentSummary ? <Text style={styles.itemHint}>{item.intentSummary}</Text> : null}
          <View style={styles.actionRow}>
            <Pressable onPress={() => handleEditInstruction(item)} style={styles.secondaryButton}>
              <Text style={styles.secondaryButtonText}>Edit</Text>
            </Pressable>
            <Pressable onPress={() => onDeleteInstruction(item.id)} style={styles.destructiveButton}>
              <Text style={styles.destructiveButtonText}>Delete</Text>
            </Pressable>
          </View>
        </View>
      ))}
      <TextInput
        value={instructionDraft.content}
        onChangeText={value => setInstructionDraft(current => ({ ...current, content: value }))}
        style={[styles.input, styles.multiline]}
        multiline
        placeholder='Explain how the agents should behave.'
        placeholderTextColor={theme.colors.inactive}
      />
      <View style={styles.actionRow}>
        <Pressable
          onPress={() => {
            onSaveInstruction(instructionDraft)
            setInstructionDraft(emptyInstructionDraft)
          }}
          style={styles.primaryButton}
        >
          <Text style={styles.primaryButtonLabel}>{instructionDraft.id ? 'Save changes' : 'Add instruction'}</Text>
        </Pressable>
        {instructionDraft.id ? (
          <Pressable onPress={() => setInstructionDraft(emptyInstructionDraft)} style={styles.secondaryButton}>
            <Text style={styles.secondaryButtonText}>Cancel</Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  )
}

export const AgentsScreen = ({
  theme,
  agents,
  workspace,
  sessionToken,
  activeTab = 'agents',
  onOpenAgent,
  onSaveCompanyProfile,
  onSaveKnowledgeItem,
  onDeleteKnowledgeItem,
  onUploadKnowledgeFile,
  onSaveInstruction,
  onDeleteInstruction,
  onSelectVoice,
  onSelectAgentVoice,
  onTabChange,
}: {
  theme: Theme
  agents: DashboardAgentSummary[]
  workspace: RelayablyWorkspaceDocument | null
  sessionToken: string | null
  activeTab?: AgentsTopTab
  onOpenAgent: (agent: DashboardAgentSummary) => void
  onSaveCompanyProfile: (payload: { businessName: string; website: string; businessDescription: string }) => void
  onSaveKnowledgeItem: (payload: { id?: string; name: string; type: 'text' | 'url'; content?: string; url?: string }) => void
  onDeleteKnowledgeItem: (knowledgeItemId: string) => void
  onUploadKnowledgeFile: (payload: { name?: string; fileName: string; mimeType?: string; base64: string }) => void
  onSaveInstruction: (payload: { id?: string; content: string }) => void
  onDeleteInstruction: (instructionId: string) => void
  onSelectVoice: (voice: RelayablyVoiceOption) => void
  onSelectAgentVoice: (agentId: string, voice: RelayablyVoiceOption | null) => void
  onTabChange?: (tab: AgentsTopTab) => void
}) => {
  const { width } = useWindowDimensions()
  const isCompact = width < 760
  const styles = createStyles(theme)
  const tabItems = [
    { key: 'agents', label: 'Agents' },
    { key: 'voice', label: 'Voice' },
    { key: 'knowledge', label: 'Knowledge' },
    { key: 'instructions', label: 'Instructions' },
  ] as const

  return (
    <View style={styles.screen}>
      {isCompact ? (
        <View style={styles.topArea}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.topTabRow}>
            {tabItems.map(tab => (
              <Pressable
                key={tab.key}
                onPress={() => onTabChange?.(tab.key as AgentsTopTab)}
                style={[styles.topTab, activeTab === tab.key ? styles.topTabActive : undefined]}
              >
                <Text style={[styles.topTabText, activeTab === tab.key ? styles.topTabTextActive : undefined]}>{tab.label}</Text>
              </Pressable>
            ))}
          </ScrollView>
        </View>
      ) : null}
      <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
        {activeTab === 'agents' ? <AgentsList theme={theme} agents={agents} onOpenAgent={onOpenAgent} /> : null}
        {activeTab === 'voice' ? (
          <VoiceManager
            theme={theme}
            sessionToken={sessionToken}
            agents={agents}
            workspace={workspace}
            selectedVoice={workspace?.workspace.voiceSelection ?? null}
            onSelectVoice={onSelectVoice}
            onSelectAgentVoice={onSelectAgentVoice}
          />
        ) : null}
        {activeTab === 'knowledge' ? (
          <KnowledgeManager
            theme={theme}
            workspace={workspace}
            onSaveCompanyProfile={onSaveCompanyProfile}
            onSaveKnowledgeItem={onSaveKnowledgeItem}
            onDeleteKnowledgeItem={onDeleteKnowledgeItem}
            onUploadKnowledgeFile={onUploadKnowledgeFile}
          />
        ) : null}
        {activeTab === 'instructions' ? (
          <InstructionsManager
            theme={theme}
            workspace={workspace}
            onSaveInstruction={onSaveInstruction}
            onDeleteInstruction={onDeleteInstruction}
          />
        ) : null}
      </ScrollView>
    </View>
  )
}

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    screen: {
      flex: 1,
    },
    topArea: {
      paddingBottom: 12,
    },
    content: {
      paddingBottom: 24,
      gap: 18,
    },
    topTabRow: {
      gap: 0,
      paddingBottom: 8,
      alignSelf: 'flex-start',
      borderRadius: 18,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.12),
      backgroundColor: hexToRgba(theme.colors.surface, 0.94),
    },
    topTab: {
      minWidth: 128,
      paddingHorizontal: 18,
      paddingVertical: 13,
      backgroundColor: 'transparent',
      alignItems: 'center',
      borderRightWidth: 1,
      borderRightColor: hexToRgba(theme.colors.primary, 0.08),
    },
    topTabActive: {
      backgroundColor: hexToRgba(theme.colors.primary, 0.16),
    },
    topTabText: {
      color: theme.colors.inactive,
      fontSize: 14,
      fontWeight: '700',
    },
    topTabTextActive: {
      color: theme.colors.text,
    },
    grid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 16,
    },
    voiceTile: {
      flexBasis: 240,
      flexGrow: 1,
      minHeight: 188,
      maxWidth: 320,
      borderRadius: 22,
      padding: 18,
      backgroundColor: hexToRgba(theme.colors.surface, 0.96),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.08),
      justifyContent: 'space-between',
      gap: 12,
    },
    voiceTileSelected: {
      borderColor: hexToRgba(theme.colors.primary, 0.32),
      shadowColor: theme.colors.primary,
      shadowOpacity: 0.18,
      shadowRadius: 16,
      shadowOffset: { width: 0, height: 10 },
    },
    voiceTileTop: {
      gap: 8,
    },
    voiceTileHeader: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 12,
    },
    voiceTileName: {
      color: theme.colors.text,
      fontFamily: 'Georgia',
      fontSize: 19,
      lineHeight: 23,
      paddingRight: 8,
      flex: 1,
    },
    voiceTagRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    voiceTag: {
      borderRadius: 999,
      paddingHorizontal: 10,
      paddingVertical: 6,
      backgroundColor: hexToRgba(theme.colors.primary, 0.12),
    },
    voiceTagText: {
      color: theme.colors.text,
      fontSize: 12,
      fontWeight: '700',
    },
    voiceTileActions: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 10,
      marginTop: 'auto',
    },
    voiceIconButtons: {
      flexDirection: 'row',
      gap: 10,
    },
    voiceSelectButton: {
      flex: 1,
      minWidth: 0,
      alignItems: 'center',
      justifyContent: 'center',
    },
    voiceSelectButtonSelected: {
      opacity: 0.9,
    },
    iconButtonPlaceholder: {
      width: 40,
      height: 40,
    },
    card: {
      flexGrow: 1,
      flexBasis: 280,
      borderRadius: 24,
      padding: 20,
      backgroundColor: hexToRgba(theme.colors.surface, 0.96),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.08),
      gap: 10,
    },
    cardSelected: {
      borderColor: hexToRgba(theme.colors.primary, 0.32),
      shadowColor: theme.colors.primary,
      shadowOpacity: 0.18,
      shadowRadius: 16,
      shadowOffset: { width: 0, height: 10 },
    },
    cardTop: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 12,
    },
    avatar: {
      width: 48,
      height: 48,
      borderRadius: 16,
      backgroundColor: hexToRgba(theme.colors.primary, 0.12),
      alignItems: 'center',
      justifyContent: 'center',
    },
    avatarText: {
      color: theme.colors.primary,
      fontSize: 18,
      fontWeight: '700',
    },
    statusBadge: {
      borderRadius: 999,
      paddingHorizontal: 10,
      paddingVertical: 5,
      backgroundColor: hexToRgba(theme.colors.primary, 0.1),
    },
    statusBadgeText: {
      color: theme.colors.primary,
      fontSize: 11,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.6,
    },
    name: {
      color: theme.colors.text,
      fontFamily: 'Georgia',
      fontSize: 22,
      lineHeight: 26,
      flex: 1,
    },
    personaLabel: {
      color: theme.colors.primary,
      fontSize: 12,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.8,
    },
    description: {
      color: theme.colors.inactive,
      fontSize: 14,
      lineHeight: 22,
    },
    metaRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
    },
    metaText: {
      color: theme.colors.text,
      fontSize: 12,
      fontWeight: '600',
    },
    voiceLabel: {
      color: theme.colors.primary,
      fontSize: 12,
      fontWeight: '700',
    },
    stack: {
      gap: 16,
    },
    panelSection: {
      position: 'relative',
      gap: 12,
      borderRadius: 24,
      padding: 20,
      backgroundColor: hexToRgba(theme.colors.surface, 0.96),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.08),
    },
    dropPanelActive: {
      borderColor: hexToRgba(theme.colors.primary, 0.32),
      backgroundColor: hexToRgba(theme.colors.primary, 0.08),
    },
    dropOverlay: {
      ...StyleSheet.absoluteFillObject,
      zIndex: 5,
      borderRadius: 24,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: hexToRgba(theme.colors.background, 0.7),
      padding: 20,
    },
    dropOverlayCard: {
      width: '100%',
      maxWidth: 420,
      borderRadius: 24,
      paddingHorizontal: 20,
      paddingVertical: 18,
      alignItems: 'center',
      gap: 8,
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.28),
      backgroundColor: hexToRgba(theme.colors.surface, 0.98),
    },
    dropOverlayCardInvalid: {
      borderColor: hexToRgba(theme.colors.notification, 0.4),
    },
    dropOverlayTitle: {
      color: theme.colors.text,
      fontSize: 18,
      fontWeight: '700',
      textAlign: 'center',
    },
    dropOverlayBody: {
      color: theme.colors.inactive,
      fontSize: 13,
      lineHeight: 20,
      textAlign: 'center',
    },
    currentVoiceBar: {
      borderRadius: 20,
      padding: 16,
      backgroundColor: hexToRgba(theme.colors.primary, 0.12),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.28),
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 16,
      shadowColor: theme.colors.primary,
      shadowOpacity: 0.14,
      shadowRadius: 18,
      shadowOffset: { width: 0, height: 10 },
    },
    currentVoiceContent: {
      flex: 1,
      gap: 8,
    },
    currentVoiceLabel: {
      color: theme.colors.primary,
      fontSize: 11,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.8,
    },
    currentVoiceName: {
      color: theme.colors.text,
      fontFamily: 'Georgia',
      fontSize: 22,
      lineHeight: 26,
    },
    sectionTitle: {
      color: theme.colors.text,
      fontFamily: 'Georgia',
      fontSize: 24,
      lineHeight: 30,
    },
    supportingCopy: {
      color: theme.colors.inactive,
      fontSize: 14,
      lineHeight: 22,
    },
    input: {
      borderRadius: 18,
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.12),
      paddingHorizontal: 16,
      paddingVertical: 14,
      color: theme.colors.text,
      backgroundColor: hexToRgba(theme.colors.background, 0.55),
      fontSize: 15,
    },
    multiline: {
      minHeight: 120,
      textAlignVertical: 'top',
    },
    actionRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 10,
    },
    primaryButton: {
      borderRadius: 999,
      backgroundColor: theme.colors.primary,
      paddingHorizontal: 18,
      paddingVertical: 12,
    },
    buttonDisabled: {
      opacity: 0.45,
    },
    primaryButtonLabel: {
      color: theme.colors.background,
      fontSize: 13,
      fontWeight: '700',
    },
    secondaryButton: {
      borderRadius: 999,
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.16),
      backgroundColor: hexToRgba(theme.colors.surface, 0.9),
      paddingHorizontal: 16,
      paddingVertical: 11,
    },
    secondaryButtonText: {
      color: theme.colors.text,
      fontSize: 13,
      fontWeight: '700',
    },
    destructiveButton: {
      borderRadius: 999,
      backgroundColor: 'rgba(183, 68, 68, 0.18)',
      paddingHorizontal: 16,
      paddingVertical: 11,
    },
    destructiveButtonText: {
      color: '#ffb6b6',
      fontSize: 13,
      fontWeight: '700',
    },
    itemCard: {
      borderRadius: 20,
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.1),
      backgroundColor: hexToRgba(theme.colors.background, 0.44),
      padding: 16,
      gap: 10,
    },
    stagedFileCard: {
      borderRadius: 20,
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.16),
      backgroundColor: hexToRgba(theme.colors.primary, 0.08),
      padding: 16,
      gap: 10,
    },
    itemHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 12,
    },
    agentVoiceHeader: {
      flex: 1,
      gap: 4,
    },
    personaInline: {
      color: theme.colors.primary,
      fontSize: 12,
      fontWeight: '700',
    },
    itemTitle: {
      color: theme.colors.text,
      fontSize: 17,
      fontWeight: '700',
      flex: 1,
    },
    itemBadge: {
      color: theme.colors.primary,
      fontSize: 11,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.8,
    },
    itemBody: {
      color: theme.colors.inactive,
      fontSize: 14,
      lineHeight: 22,
    },
    itemHint: {
      color: theme.colors.primary,
      fontSize: 12,
      lineHeight: 18,
    },
    selectedBadge: {
      borderRadius: 999,
      backgroundColor: hexToRgba(theme.colors.primary, 0.16),
      paddingHorizontal: 10,
      paddingVertical: 5,
    },
    selectedBadgeText: {
      color: theme.colors.primary,
      fontSize: 11,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.7,
    },
    iconButton: {
      width: 40,
      height: 40,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.16),
      backgroundColor: hexToRgba(theme.colors.surface, 0.9),
      alignItems: 'center',
      justifyContent: 'center',
    },
    toggleRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 10,
    },
    chipRow: {
      gap: 10,
    },
    toggleChip: {
      borderRadius: 999,
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.12),
      paddingHorizontal: 14,
      paddingVertical: 9,
      backgroundColor: hexToRgba(theme.colors.surface, 0.84),
    },
    toggleChipActive: {
      backgroundColor: hexToRgba(theme.colors.primary, 0.14),
      borderColor: hexToRgba(theme.colors.primary, 0.28),
    },
    toggleChipText: {
      color: theme.colors.inactive,
      fontSize: 13,
      fontWeight: '700',
    },
    toggleChipTextActive: {
      color: theme.colors.text,
    },
    filterBlock: {
      gap: 10,
      borderRadius: 20,
      padding: 14,
      backgroundColor: hexToRgba(theme.colors.surface, 0.72),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.08),
    },
    filterBlockWide: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 16,
    },
    filterBlockMedium: {
      gap: 12,
    },
    filterSection: {
      gap: 8,
      minWidth: 0,
    },
    filterSectionWide: {
      flex: 1.1,
    },
    filterSectionStacked: {
      flex: 1,
      gap: 10,
    },
    filterRow: {
      gap: 6,
    },
    filterLabel: {
      color: theme.colors.primary,
      fontSize: 11,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.8,
    },
    filterInput: {
      borderRadius: 14,
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.12),
      paddingHorizontal: 14,
      paddingVertical: 11,
      color: theme.colors.text,
      backgroundColor: hexToRgba(theme.colors.background, 0.5),
      fontSize: 14,
    },
    filterInputCompact: {
      paddingVertical: 10,
      fontSize: 13,
    },
    compactChipRow: {
      gap: 8,
      paddingRight: 4,
    },
    compactToggleRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    filterPill: {
      borderRadius: 999,
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.1),
      paddingHorizontal: 11,
      paddingVertical: 7,
      backgroundColor: hexToRgba(theme.colors.surface, 0.88),
    },
    filterPillActive: {
      backgroundColor: hexToRgba(theme.colors.primary, 0.14),
      borderColor: hexToRgba(theme.colors.primary, 0.26),
    },
    filterPillText: {
      color: theme.colors.inactive,
      fontSize: 12,
      fontWeight: '700',
    },
    filterPillTextActive: {
      color: theme.colors.text,
    },
    advancedToggle: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 12,
    },
    advancedToggleText: {
      color: theme.colors.primary,
      fontSize: 13,
      fontWeight: '700',
    },
    loaderWrap: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 32,
    },
    errorText: {
      color: '#ffb6b6',
      fontSize: 13,
      lineHeight: 20,
    },
  })
